import qs from 'qs'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import SearchPlace from './SearchPlace'

import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useTripMetadataPrefetch } from 'tripPlanner/hooks/api/trip'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { PlaceInputTrigger } from './Common/PlaceInputTrigger'

interface Props {
  tripId: string
  sectionId?: string
}

function SearchExperience({ tripId, sectionId }: Props) {
  const intl = useIntl()
  useTripMetadataPrefetch({ tripId })
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()
  const { setImmersiveMode } = useImmersiveModeHandlers()

  const { searchItem } = useContext(GlobalSearchStateContext)
  const source = useTripClientSource()
  const query = useMemo(
    () =>
      qs.stringify({
        destinationId: searchItem ?
          searchItem.value :
          ANYWHERE_SEARCH_ITEM.value,
        destinationName: searchItem ?
          searchItem.format.mainText :
          ANYWHERE_SEARCH_ITEM.format.mainText,
        source,
      }),
    [searchItem, source],
  )

  const onPrimaryActionClick = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('experiences_search'))
    setImmersiveMode({ tripId, sectionId })
  }, [setImmersiveMode, sectionId, tripId])

  const isMobile = useScreenSizeOnly('mobile')

  const modalTitle = intl.formatMessage({
    id: 'X5BxJVw71Z',
    defaultMessage: 'Search experiences',
    description: 'Modal title for experience search in trip planner',
  })

  const bodyDescription = intl.formatMessage({
    id: 'kfI4WVFrow',
    defaultMessage: 'Find the worlds best experiences in any destination',
    description: 'Promotional description text for experience search in trip planner',
  })

  const defaultSubTitle = intl.formatMessage({
    id: '5Px52b1kkJ',
    defaultMessage: 'Search by destination',
    description: 'Default placeholder text for destination search input in experience search',
  })

  const searchButtonLabel = intl.formatMessage({
    id: 'jlWUul1mKp',
    defaultMessage: 'Search',
    description: 'Button text to submit experience search in trip planner',
  })

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            <BodyText variant="large">
              {bodyDescription}
            </BodyText>
            <PlaceInputTrigger
              subTitle={searchItem?.format.mainText || defaultSubTitle}
              onClick={() =>
                openModal(
                  SearchPlace,
                  {
                    searchView: 'EXPERIENCE' as const,
                    tripId,
                  },
                  {
                    height: 'full',
                  },
                )}
            />
            <Group direction="horizontal-reverse">
              <TextButton
                kind="primary"
                fit={isMobile ? 'full-width' : undefined}
                to={'/search/experiences?' + query}
                onClick={onPrimaryActionClick}
                size="large"
              >
                {searchButtonLabel}
              </TextButton>
            </Group>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SearchExperience
